<template>
  <div class="blend-property" style="--blend-type-columns: 5" data-testid="chamfer-property-section">
    <n-collapse :default-expanded-names="defaultExpanded" class="pm-collapse">
      <PuppyCollapseItem title="倒角类型" name="type">
        <div class="type-grid" data-testid="chamfer-type-grid">
          <button
            v-for="option in chamferTypeOptions"
            :key="option.value"
            type="button"
            class="type-card"
            :class="{ 'is-active': chamferType === option.value }"
            :title="option.title"
            :data-testid="option.testId"
            @click="handleChamferTypeChange(option.value)"
          >
            <BlendTypeGlyph :icon-key="option.iconKey" />
          </button>
        </div>
      </PuppyCollapseItem>

      <PuppyCollapseItem title="要倒角化的项目" name="items">
        <div class="section-grid" data-testid="chamfer-items-panel">
          <template v-if="showFaceSetBuckets">
            <PuppyFormItem>
              <template #icon>
                <span class="field-label-text">面组1</span>
              </template>
              <PuppyInputSelect
                :items="faceSet1Items"
                :active="chamferSelectionTarget === 'faces' && activeFaceSetId === 'faceSet1'"
                :active-index="resolveActiveIndex(faceSet1Items, selectedChamferItemRef)"
                :selected-indices="resolveSelectedIndices(faceSet1Items, selectedChamferItemRef)"
                :visible-rows="3"
                item-key-field="persistentRef"
                item-label-field="label"
                empty-text="尚未选择面组 1"
                @update:active="(active) => handleFaceSetInputActiveChange('faceSet1', active)"
                @item-click="handleFaceSetItemClick('faceSet1', $event.item)"
                @delete-item="handleFaceSetItemRemove('faceSet1', $event.item)"
                @delete-all="handleFaceSetDeleteAll('faceSet1')"
              />
            </PuppyFormItem>

            <PuppyFormItem>
              <template #icon>
                <span class="field-label-text">面组2</span>
              </template>
              <PuppyInputSelect
                :items="faceSet2Items"
                :active="chamferSelectionTarget === 'faces' && activeFaceSetId === 'faceSet2'"
                :active-index="resolveActiveIndex(faceSet2Items, selectedChamferItemRef)"
                :selected-indices="resolveSelectedIndices(faceSet2Items, selectedChamferItemRef)"
                :visible-rows="3"
                item-key-field="persistentRef"
                item-label-field="label"
                empty-text="尚未选择面组 2"
                @update:active="(active) => handleFaceSetInputActiveChange('faceSet2', active)"
                @item-click="handleFaceSetItemClick('faceSet2', $event.item)"
                @delete-item="handleFaceSetItemRemove('faceSet2', $event.item)"
                @delete-all="handleFaceSetDeleteAll('faceSet2')"
              />
            </PuppyFormItem>
          </template>

          <template v-else>
            <PuppyFormItem>
              <template #icon>
                <span class="field-label-text">项目</span>
              </template>
              <PuppyInputSelect
                :items="selectedItems"
                :active="chamferSelectionTarget === 'faces'"
                :active-index="resolveActiveIndex(selectedItems, selectedChamferItemRef)"
                :selected-indices="resolveSelectedIndices(selectedItems, selectedChamferItemRef)"
                :visible-rows="3"
                item-key-field="persistentRef"
                item-label-field="label"
                empty-text="尚未选择边线、面或顶点"
                @update:active="handlePrimaryInputActiveChange"
                @item-click="handlePrimaryItemClick($event.item)"
                @delete-item="handlePrimaryItemRemove($event.item)"
                @delete-all="handlePrimaryDeleteAll"
              />
            </PuppyFormItem>
          </template>

        </div>
      </PuppyCollapseItem>

      <PuppyCollapseItem title="倒角参数" name="parameters">
        <div class="section-grid" data-testid="chamfer-parameters-panel">
          <PuppyFormItem v-if="chamferType === 'distance-distance'">
            <template #icon>
              <span class="field-label-text">模式</span>
            </template>
            <n-select
              size="small"
              :value="distanceDistanceMode"
              :options="distanceDistanceModeOptions"
              :disabled="!context.setChamferSymmetric"
              @update:value="handleDistanceDistanceModeChange"
            />
          </PuppyFormItem>

          <PuppyFormItem v-if="chamferType === 'offset-face'">
            <template #icon>
              <span class="field-label-text">模式</span>
            </template>
            <n-select
              size="small"
              :value="offsetFaceMode"
              :options="offsetFaceModeOptions"
              :disabled="offsetFaceModeLocked || !context.setChamferSymmetric"
              @update:value="handleOffsetFaceModeChange"
            />
          </PuppyFormItem>

          <PuppyFormItem v-if="chamferType === 'face-face' && isFieldVisible('faceFaceModes')">
            <template #icon>
              <span class="field-label-text">模式</span>
            </template>
            <n-select
              size="small"
              :value="chamferFaceFaceMode"
              :options="faceFaceModeOptions"
              :disabled="!context.setChamferFaceFaceMode"
              @update:value="handleFaceFaceModeChange"
            />
          </PuppyFormItem>

          <PuppyFormItem v-if="showDistanceField">
            <template #icon>
              <span class="field-label-text">距离</span>
            </template>
            <div class="field-with-unit">
              <n-input-number
                size="small"
                show-button
                :value="distanceValue"
                :disabled="!context.setChamferDistance"
                :min="CHAMFER_DISTANCE_MIN"
                :max="CHAMFER_DISTANCE_MAX"
                :precision="3"
                :step="1"
                @update:value="handleDistanceCommit"
              />
              <span class="field-unit">mm</span>
            </div>
          </PuppyFormItem>

          <PuppyFormItem v-if="showDistance2Field">
            <template #icon>
              <span class="field-label-text">距离 2</span>
            </template>
            <div class="field-with-unit">
              <n-input-number
                size="small"
                show-button
                :value="distance2Value"
                :disabled="!context.setChamferDistance2"
                :min="CHAMFER_DISTANCE_MIN"
                :max="CHAMFER_DISTANCE_MAX"
                :precision="3"
                :step="1"
                @update:value="handleDistance2Commit"
              />
              <span class="field-unit">mm</span>
            </div>
          </PuppyFormItem>

          <PuppyFormItem v-if="isChordWidthMode">
            <template #icon>
              <span class="field-label-text">弦宽度</span>
            </template>
            <div class="field-with-unit">
              <n-input-number
                size="small"
                show-button
                :value="widthValue"
                :disabled="!context.setChamferWidth"
                :min="CHAMFER_DISTANCE_MIN"
                :max="CHAMFER_DISTANCE_MAX"
                :precision="3"
                :step="1"
                @update:value="handleWidthCommit"
              />
              <span class="field-unit">mm</span>
            </div>
          </PuppyFormItem>

          <PuppyFormItem v-if="showAngleField">
            <template #icon>
              <span class="field-label-text">角度</span>
            </template>
            <div class="field-with-unit">
              <n-input-number
                size="small"
                show-button
                :value="angleValue"
                :disabled="!context.setChamferAngleDeg"
                :min="CHAMFER_ANGLE_MIN"
                :max="CHAMFER_ANGLE_MAX"
                :precision="3"
                :step="1"
                @update:value="handleAngleCommit"
              />
              <span class="field-unit">deg</span>
            </div>
          </PuppyFormItem>

          <PuppyFormItem v-if="showFlipDirectionField">
            <template #icon>
              <span class="field-label-text">方向</span>
            </template>
            <n-checkbox
              :checked="flipDirection"
              :disabled="!isFieldEnabled('flipDirection')"
              @update:checked="(value) => context.setChamferFlipDirection?.(value)"
            >
              反转方向
            </n-checkbox>
          </PuppyFormItem>

          <PuppyFormItem v-if="chamferType === 'vertex'">
            <template #icon>
              <span class="field-label-text">等距</span>
            </template>
            <n-checkbox
              :checked="equalDistance"
              :disabled="!context.setChamferEqualDistance"
              @update:checked="(value) => context.setChamferEqualDistance?.(value)"
            >
              等距倒角
            </n-checkbox>
          </PuppyFormItem>

          <PuppyFormItem v-if="chamferType === 'vertex' && equalDistance && isFieldVisible('vertexDistances')">
            <template #icon>
              <span class="field-label-text">距离</span>
            </template>
            <div class="field-with-unit">
              <n-input-number
                size="small"
                show-button
                :value="distanceValue"
                :disabled="!context.setChamferDistance"
                :min="CHAMFER_DISTANCE_MIN"
                :max="CHAMFER_DISTANCE_MAX"
                :precision="3"
                :step="1"
                @update:value="handleDistanceCommit"
              />
              <span class="field-unit">mm</span>
            </div>
          </PuppyFormItem>

          <div
            v-if="chamferType === 'vertex' && !equalDistance && isFieldVisible('vertexDistances')"
            class="section-grid"
          >
            <PuppyFormItem
              v-for="(distance, index) in vertexDistances"
              :key="`vertex-distance-${index}`"
            >
              <template #icon>
                <span class="field-label-text">距离{{ index + 1 }}</span>
              </template>
              <div class="field-with-unit">
                <n-input-number
                  size="small"
                  show-button
                  :value="formatLengthForPropertyManager(distance)"
                  :disabled="!context.setChamferVertexDistance || (equalDistance && index > 0)"
                  :min="CHAMFER_DISTANCE_MIN"
                  :max="CHAMFER_DISTANCE_MAX"
                  :precision="3"
                  :step="1"
                  @update:value="(value) => handleVertexDistanceCommit(index, value)"
                />
                <span class="field-unit">mm</span>
              </div>
            </PuppyFormItem>
          </div>
        </div>
      </PuppyCollapseItem>

      <PartialEdgeParametersSection
        v-if="showPartialEdgeParametersPanel"
        :enabled="partialEdgeParametersEnabled"
        :editable="isFieldEnabled('partialEdgeParameters')"
        :partial-edges="selectedChamferPartialEdges"
        :selected-items="selectedItems"
        :active-reference-target="partialReferenceTarget"
        @update:enabled="(value) => context.setChamferPartialEdgeParametersEnabled?.(value)"
        @condition-change="handlePartialConditionChange"
        @value-commit="handlePartialValueCommit"
        @reference-active-change="handlePartialReferenceActiveChange"
        @reference-delete="handlePartialReferenceDelete"
      />

    </n-collapse>
  </div>
</template>

<script setup lang="ts">
import { computed, toRef } from 'vue';
import {
  NCheckbox,
  NCollapse,
  NInputNumber,
  NSelect,
} from 'naive-ui';
import type {
  BlendPartialCondition,
  ChamferFaceFaceMode,
  ChamferType,
} from '@puppycad/cad-core';
import {
  convertMToMm,
  convertMmToM,
} from '@puppycad/cad-core';
import type { PropertyManagerContext } from '@/core';
import type { BlendPartialReferenceTarget } from '@/core/features/blend/partial-edge';
import type {
  BlendFieldAvailability,
  ChamferPropertyPanelSpec,
} from '@/core/features/blend/panel-spec';
import { resolveChamferPropertyPanelSpec } from '@/core/features/blend/panel-spec';
import PuppyCollapseItem from '@/components/PuppyCollapseItem/PuppyCollapseItem.vue';
import PuppyFormItem from '@/components/PuppyFormItem/PuppyFormItem.vue';
import PuppyInputSelect from '@/components/PuppyInputSelect/PuppyInputSelect.vue';
import BlendTypeGlyph from '../common/BlendTypeGlyph.vue';
import PartialEdgeParametersSection from './PartialEdgeParametersSection.vue';

interface ChamferPropertySectionProps {
  context: PropertyManagerContext;
}

type ChamferPanelField = keyof ChamferPropertyPanelSpec;
type SelectablePersistentItem = {
  persistentRef: string;
  label: string;
  kind?: string;
};

const props = defineProps<ChamferPropertySectionProps>();
const context = toRef(props, 'context');

const defaultExpanded = ['type', 'items', 'parameters', 'partial-edge'];
const CHAMFER_DISTANCE_MIN = 0.001;
const CHAMFER_DISTANCE_MAX = 1_000_000;
const CHAMFER_DISTANCE_MIN_M = convertMmToM(CHAMFER_DISTANCE_MIN);
const CHAMFER_DISTANCE_MAX_M = convertMmToM(CHAMFER_DISTANCE_MAX);
const CHAMFER_ANGLE_MIN = 0;
const CHAMFER_ANGLE_MAX = 180;

const chamferTypeOptions = [
  { value: 'angle-distance' as const, iconKey: 'chamfer-angle-distance' as const, title: '角度-距离倒角', testId: 'chamfer-angle-distance-type' },
  { value: 'distance-distance' as const, iconKey: 'chamfer-distance-distance' as const, title: '距离-距离倒角', testId: 'chamfer-distance-distance-type' },
  { value: 'vertex' as const, iconKey: 'chamfer-vertex' as const, title: '顶点倒角', testId: 'chamfer-vertex-type' },
  { value: 'offset-face' as const, iconKey: 'chamfer-offset-face' as const, title: '等距面倒角', testId: 'chamfer-offset-face-type' },
  { value: 'face-face' as const, iconKey: 'chamfer-face-face' as const, title: '面-面倒角', testId: 'chamfer-face-face-type' },
] satisfies ReadonlyArray<{
  value: ChamferType;
  iconKey: 'chamfer-angle-distance' | 'chamfer-distance-distance' | 'chamfer-vertex' | 'chamfer-offset-face' | 'chamfer-face-face';
  title: string;
  testId: string;
}>;

const distanceDistanceModeOptions = [
  { label: '对称', value: 'symmetric' },
  { label: '非对称', value: 'asymmetric' },
];

const offsetFaceModeOptions = [
  { label: '对称', value: 'symmetric' },
  { label: '非对称', value: 'asymmetric' },
];

const faceFaceModeOptions = [
  { label: '对称', value: 'symmetric' },
  { label: '非对称', value: 'asymmetric' },
  { label: '弦宽度', value: 'chord-width', disabled: false },
];

const chamferType = computed<ChamferType>(() => context.value.chamferType ?? 'angle-distance');
const distanceValue = computed(() => formatLengthForPropertyManager(context.value.chamferDistance ?? convertMmToM(10)));
const distance2Value = computed(() => formatLengthForPropertyManager(context.value.chamferDistance2 ?? convertMmToM(10)));
const widthValue = computed(() => formatLengthForPropertyManager(context.value.chamferWidth ?? context.value.chamferDefinition?.width ?? convertMmToM(10)));
const angleValue = computed(() => context.value.chamferAngleDeg ?? 45);
const symmetric = computed(() => context.value.chamferSymmetric !== false);
const flipDirection = computed(() => context.value.chamferFlipDirection === true);
const equalDistance = computed(() => context.value.chamferEqualDistance !== false);
const vertexDistances = computed(() => context.value.chamferVertexDistances ?? [convertMmToM(10), convertMmToM(10), convertMmToM(10)]);
const partialEdges = computed(() => context.value.chamferPartialEdges ?? []);
const selectedItems = computed(() => context.value.chamferItems ?? []);
const selectedChamferItemRef = computed(() => context.value.selectedChamferItemRef ?? null);
const partialEdgeParametersEnabled = computed(() => context.value.chamferDefinition?.partialEdgeParametersEnabled === true);
const partialReferenceTarget = computed<BlendPartialReferenceTarget | null>(() => context.value.chamferPartialReferenceTarget ?? null);
const hasFaceSelectionInOffsetFaceItems = computed(() => (
  chamferType.value === 'offset-face'
  && selectedItems.value.some((item) => item.kind === 'face')
));
const selectedChamferEdgeItem = computed(() => (
  selectedItems.value.find((item) => (
    item.persistentRef === selectedChamferItemRef.value
    && item.kind === 'edge'
  )) ?? null
));
const selectedChamferPartialEdges = computed(() => (
  selectedChamferEdgeItem.value
    ? (
      partialEdges.value.some((partialEdge) => partialEdge.itemRef === selectedChamferEdgeItem.value?.persistentRef)
        ? partialEdges.value.filter((partialEdge) => partialEdge.itemRef === selectedChamferEdgeItem.value?.persistentRef)
        : [{
          itemRef: selectedChamferEdgeItem.value.persistentRef,
          startCondition: 'none' as const,
          endCondition: 'none' as const,
        }]
    )
    : []
));
const showPartialEdgeParametersPanel = computed(() => (
  isFieldVisible('partialEdgeParameters')
  && selectedChamferEdgeItem.value !== null
  && selectedChamferPartialEdges.value.length > 0
));
const selectedItemsByRef = computed(() => new Map(selectedItems.value.map((item) => [item.persistentRef, item])));
const supportSpec = computed(() => resolveChamferPropertyPanelSpec(chamferType.value));
const chamferFaceFaceMode = computed<ChamferFaceFaceMode>(
  () => context.value.chamferFaceFaceMode ?? context.value.chamferDefinition?.faceFaceMode ?? 'symmetric',
);
const isChordWidthMode = computed(
  () => chamferType.value === 'face-face' && chamferFaceFaceMode.value === 'chord-width',
);
const isFaceFaceAsymmetric = computed(
  () => chamferType.value === 'face-face' && chamferFaceFaceMode.value === 'asymmetric',
);
const showDistanceField = computed(() => !isChordWidthMode.value && chamferType.value !== 'vertex');
const showDistance2Field = computed(
  () =>
    (chamferType.value === 'distance-distance' && distanceDistanceMode.value === 'asymmetric')
    || (chamferType.value === 'offset-face' && offsetFaceMode.value === 'asymmetric')
    || isFaceFaceAsymmetric.value,
);
/**
 * 判断是否显示“反转方向”。
 *
 * 设计说明：
 * 1. 角度-距离倒角始终通过支撑面翻转方向；
 * 2. 等距面倒角只有非对称模式才有第二支撑面方向含义；
 * 3. 对称等距面倒角隐藏该项，避免 UI 暴露无效参数。
 */
const showFlipDirectionField = computed(() => (
  isFieldVisible('flipDirection')
  && (
    chamferType.value === 'angle-distance'
    || (chamferType.value === 'offset-face' && offsetFaceMode.value === 'asymmetric')
    || isFaceFaceAsymmetric.value
  )
));
const showFaceSetBuckets = computed(
  () => chamferType.value === 'face-face' && isFieldVisible('faceSetSelection'),
);
const showAngleField = computed(
  () => chamferType.value === 'angle-distance',
);
const activeFaceSetId = computed(() => context.value.chamferActiveFaceSetId ?? 'faceSet1');
const chamferSelectionTarget = computed(() => context.value.chamferSelectionTarget ?? 'faces');
const faceSet1Items = computed(() => resolveFaceSetItems('faceSet1'));
const faceSet2Items = computed(() => resolveFaceSetItems('faceSet2'));
const distanceDistanceMode = computed(() => (symmetric.value ? 'symmetric' : 'asymmetric'));
const offsetFaceModeLocked = computed(() => hasFaceSelectionInOffsetFaceItems.value);
const offsetFaceMode = computed(() => (
  offsetFaceModeLocked.value || symmetric.value ? 'symmetric' : 'asymmetric'
));

/**
 * 读取字段可用性。
 *
 * @param field 字段标识。
 * @returns 字段可用性。
 */
function resolveFieldAvailability(field: ChamferPanelField): BlendFieldAvailability {
  return supportSpec.value[field];
}

/**
 * 判断字段是否可见。
 *
 * @param field 字段标识。
 * @returns 是否可见。
 */
function isFieldVisible(field: ChamferPanelField): boolean {
  return resolveFieldAvailability(field) !== 'hidden';
}

/**
 * 判断字段是否可编辑。
 *
 * @param field 字段标识。
 * @returns 是否可编辑。
 */
function isFieldEnabled(field: ChamferPanelField): boolean {
  return resolveFieldAvailability(field) === 'enabled';
}

/**
 * 按持久化引用解析激活索引。
 *
 * @param items 输入列表。
 * @param persistentRef 持久化引用。
 * @returns 激活索引；缺失时返回 -1。
 */
function resolveActiveIndex(
  items: SelectablePersistentItem[],
  persistentRef: string | null,
): number {
  if (!persistentRef) {
    return -1;
  }
  return items.findIndex((item) => item.persistentRef === persistentRef);
}

/**
 * 按持久化引用解析选中索引集合。
 *
 * @param items 输入列表。
 * @param persistentRef 持久化引用。
 * @returns 选中索引集合。
 */
function resolveSelectedIndices(
  items: SelectablePersistentItem[],
  persistentRef: string | null,
): number[] {
  const index = resolveActiveIndex(items, persistentRef);
  return index >= 0 ? [index] : [];
}

/**
 * 切换倒角类型。
 *
 * @param type 目标倒角类型。
 */
function handleChamferTypeChange(type: ChamferType): void {
  context.value.setChamferType?.(type);
}

/**
 * 激活主选择输入框。
 *
 * @param active 是否激活。
 */
function handlePrimaryInputActiveChange(active: boolean): void {
  if (active) {
    context.value.setChamferSelectionTarget?.('faces');
  }
}

/**
 * 激活面组输入框。
 *
 * @param setId 目标面组。
 * @param active 是否激活。
 */
function handleFaceSetInputActiveChange(setId: 'faceSet1' | 'faceSet2', active: boolean): void {
  if (!active) {
    return;
  }
  context.value.setChamferFaceSetTarget?.(setId);
  context.value.setChamferSelectionTarget?.('faces');
}

/**
 * 处理主列表点击。
 *
 * @param item 被点击的列表项。
 */
function handlePrimaryItemClick(item: unknown): void {
  const selectionItem = item as SelectablePersistentItem;
  context.value.selectChamferItem?.(selectionItem.persistentRef);
}

/**
 * 删除主列表项。
 *
 * @param item 被删除的列表项。
 */
function handlePrimaryItemRemove(item: unknown): void {
  const selectionItem = item as SelectablePersistentItem;
  context.value.removeChamferItem?.(selectionItem.persistentRef);
}

/**
 * 清空主列表。
 */
function handlePrimaryDeleteAll(): void {
  selectedItems.value.forEach((item) => {
    context.value.removeChamferItem?.(item.persistentRef);
  });
}

/**
 * 处理面组项点击。
 *
 * @param setId 当前面组。
 * @param item 被点击的列表项。
 */
function handleFaceSetItemClick(setId: 'faceSet1' | 'faceSet2', item: unknown): void {
  const selectionItem = item as SelectablePersistentItem;
  context.value.setChamferFaceSetTarget?.(setId);
  context.value.selectChamferItem?.(selectionItem.persistentRef);
}

/**
 * 处理面组项删除。
 *
 * @param setId 当前面组。
 * @param item 被删除的列表项。
 */
function handleFaceSetItemRemove(setId: 'faceSet1' | 'faceSet2', item: unknown): void {
  const selectionItem = item as SelectablePersistentItem;
  context.value.setChamferFaceSetTarget?.(setId);
  context.value.removeChamferItem?.(selectionItem.persistentRef);
}

/**
 * 清空指定面组。
 *
 * @param setId 目标面组。
 */
function handleFaceSetDeleteAll(setId: 'faceSet1' | 'faceSet2'): void {
  resolveFaceSetItems(setId).forEach((item) => {
    context.value.setChamferFaceSetTarget?.(setId);
    context.value.removeChamferItem?.(item.persistentRef);
  });
}

/**
 * 切换距离-距离模式。
 *
 * @param value 目标模式。
 */
function handleDistanceDistanceModeChange(value: string | null): void {
  if (value === 'symmetric') {
    context.value.setChamferSymmetric?.(true);
  }
  if (value === 'asymmetric') {
    context.value.setChamferSymmetric?.(false);
  }
}

/**
 * 切换等距面倒角模式。
 *
 * @param value 目标模式。
 */
function handleOffsetFaceModeChange(value: string | null): void {
  if (offsetFaceModeLocked.value) {
    context.value.setChamferSymmetric?.(true);
    return;
  }
  if (value === 'symmetric') {
    context.value.setChamferSymmetric?.(true);
  }
  if (value === 'asymmetric') {
    context.value.setChamferSymmetric?.(false);
  }
}

//------ 倒角属性长度单位转换 开始------
/**
 * 将底层米制长度转换为 PropertyManager 的毫米显示值。
 *
 * @param value 底层米制长度。
 * @returns 毫米显示值。
 */
function formatLengthForPropertyManager(value: number): number {
  // 倒角参数在 UI 层显示毫米，底层几何仍按米参与计算。
  return Number(convertMToMm(value).toFixed(3));
}

/**
 * 将 PropertyManager 的毫米输入转换为底层米制长度。
 *
 * @param value 用户输入的毫米值。
 * @returns 米制长度。
 */
function normalizeLengthFromPropertyManager(value: number | null): number {
  const normalizedMm = clampDistanceValue(value);
  return Math.min(CHAMFER_DISTANCE_MAX_M, Math.max(CHAMFER_DISTANCE_MIN_M, convertMmToM(normalizedMm)));
}
//------ 倒角属性长度单位转换 结束------

/**
 * 限制倒角距离类输入范围。
 *
 * @param value 原始输入值。
 * @returns 已限制范围的距离值。
 */
function clampDistanceValue(value: number | null): number {
  return Math.min(CHAMFER_DISTANCE_MAX, Math.max(CHAMFER_DISTANCE_MIN, value ?? CHAMFER_DISTANCE_MIN));
}

/**
 * 限制倒角角度输入范围。
 *
 * @param value 原始输入值。
 * @returns 已限制范围的角度值。
 */
function clampAngleValue(value: number | null): number {
  return Math.min(CHAMFER_ANGLE_MAX, Math.max(CHAMFER_ANGLE_MIN, value ?? CHAMFER_ANGLE_MIN));
}

/**
 * 提交距离。
 *
 * @param value 输入值。
 */
function handleDistanceCommit(value: number | null): void {
  context.value.setChamferDistance?.(normalizeLengthFromPropertyManager(value), null);
}

/**
 * 提交第二距离。
 *
 * @param value 输入值。
 */
function handleDistance2Commit(value: number | null): void {
  context.value.setChamferDistance2?.(normalizeLengthFromPropertyManager(value), null);
}

/**
 * 提交宽度。
 *
 * @param value 输入值。
 */
function handleWidthCommit(value: number | null): void {
  context.value.setChamferWidth?.(normalizeLengthFromPropertyManager(value), null);
}

/**
 * 提交角度。
 *
 * @param value 输入值。
 */
function handleAngleCommit(value: number | null): void {
  context.value.setChamferAngleDeg?.(clampAngleValue(value), null);
}

/**
 * 提交顶点距离。
 *
 * @param index 顶点索引。
 * @param value 输入值。
 */
function handleVertexDistanceCommit(index: number, value: number | null): void {
  if (index === 0 || index === 1 || index === 2) {
    context.value.setChamferVertexDistance?.(index, normalizeLengthFromPropertyManager(value), null);
  }
}

/**
 * 切换局部边条件。
 *
 * @param itemRef 对应边引用。
 * @param side 起止侧。
 * @param value 目标条件。
 */
function handlePartialConditionChange(
  itemRef: string,
  side: 'start' | 'end',
  value: BlendPartialCondition,
): void {
  context.value.setChamferPartialCondition?.(itemRef, side, value);
}

/**
 * 提交局部边数值。
 *
 * @param itemRef 对应边引用。
 * @param side 起止侧。
 * @param value 输入值。
 */
function handlePartialValueCommit(
  itemRef: string,
  side: 'start' | 'end',
  value: number,
): void {
  context.value.setChamferPartialValue?.(itemRef, side, Math.max(0, value), null);
}

/**
 * 切换部分边线参考选择输入框。
 *
 * @param itemRef 对应边引用。
 * @param side 起止侧。
 * @param active 是否激活。
 */
function handlePartialReferenceActiveChange(
  itemRef: string,
  side: 'start' | 'end',
  active: boolean,
): void {
  context.value.setChamferPartialReferenceTarget?.(itemRef, side, active);
}

/**
 * 清空部分边线参考。
 *
 * @param itemRef 对应边引用。
 * @param side 起止侧。
 */
function handlePartialReferenceDelete(
  itemRef: string,
  side: 'start' | 'end',
): void {
  context.value.clearChamferPartialReference?.(itemRef, side);
}

/**
 * 切换面-面倒角模式。
 *
 * @param mode 目标模式。
 */
function handleFaceFaceModeChange(mode: string | null): void {
  if (mode === 'symmetric' || mode === 'asymmetric' || mode === 'chord-width') {
    context.value.setChamferFaceFaceMode?.(mode);
  }
}

/**
 * 解析面组内显示项。
 *
 * @param setId 面组标识。
 * @returns 面组显示项列表。
 */
function resolveFaceSetItems(setId: 'faceSet1' | 'faceSet2'): SelectablePersistentItem[] {
  return (
    context.value.chamferDefinition?.faceSets
      ?.find((faceSet) => faceSet.setId === setId)
      ?.items
      ?.map((item, index) => selectedItemsByRef.value.get(item.persistentRef) ?? {
        persistentRef: item.persistentRef,
        label: `面<${index + 1}>`,
      })
    ?? []
  );
}

</script>

<style scoped src="./BlendPropertySection.css"></style>
